import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button, TextButton } from '@xingternal/button';
import { IllustrationSpotBell } from '@xingternal/illustrations';
import { Link } from '@xingternal/link';
import { PopUpWindow } from '@xingternal/pop-up-window';
import { FormField } from '@xingternal/text-field';

import { FiltersSummary } from '@/components/filters-summary';
import { routes } from '@/config';
import { Summary } from '@/contexts';
import { isValidEmail } from '@/helpers/validations';

import {
  CreateAlertLightboxButtonsWrapper,
  CreateAlertLightboxContent,
  CreateAlertLightboxHeadline,
  CreateAlertLightboxLOContent,
  CreateAlertLightboxParagraph,
} from '@/components/popup-handler/components/create-alert-lightbox/create-alert-lightbox-component.styles';

type Props = {
  summary: Summary;
  email: string;
  onCancel: (action: string) => () => void;
  onCreate: () => void;
  creatingAlert: boolean;
  isAlertLightboxOpen: boolean;
  didCreateAlert: boolean;
  onChangeEmail: React.Dispatch<React.SetStateAction<string>>;
  isLoggedOut: boolean;
};

export const CreateAlertLightbox: React.VFC<Props> = ({
  summary,
  email,
  onCancel,
  onCreate,
  creatingAlert,
  isAlertLightboxOpen,
  didCreateAlert,
  onChangeEmail,
  isLoggedOut,
}) => {
  const intl = useIntl();
  const [showEmailError, setShowEmailError] = React.useState(false);

  const handleOnCreate = (): void => {
    if (isLoggedOut) {
      const hasError = !isValidEmail(email);
      setShowEmailError(hasError);

      if (hasError) return;
    }

    onCreate();
  };

  return (
    <PopUpWindow
      onDimmerClick={onCancel('cancel')}
      hide={onCancel('cancel')}
      isShown={isAlertLightboxOpen && !creatingAlert}
      noPadding
    >
      <CreateAlertLightboxContent>
        <IllustrationSpotBell size="large" />
        <div>
          <CreateAlertLightboxHeadline size="xxlarge">
            <FormattedMessage
              id={
                didCreateAlert && !isLoggedOut
                  ? 'JOBS_POSTING_CREATED_ALERT_LIGHTBOX_HEADER'
                  : 'JOBS_POSTING_CREATE_ALERT_LIGHTBOX_HEADER'
              }
            />
          </CreateAlertLightboxHeadline>
          <CreateAlertLightboxParagraph>
            {didCreateAlert ? (
              isLoggedOut ? (
                <FormattedMessage id="JOBS_CREATE_SEARCH_ALERT_LIGHTBOX_SUBSCRIPTION_CONFIRM" />
              ) : (
                <FormattedMessage
                  id="JOBS_POSTING_CREATED_ALERT_LIGHTBOX_PARAGRAPH_TEMP"
                  values={{
                    email,
                    b: (chunks) => <b>{chunks}</b>,
                    a: (chunks) => (
                      <Link
                        to={`${routes.visionTypeAlerts}&sc_o=jobs_manage_search_alerts&sc_o_PropActionOrigin=jobs_jdp`}
                      >
                        {chunks}
                      </Link>
                    ),
                  }}
                />
              )
            ) : (
              <FormattedMessage id="JOBS_CREATE_SEARCH_ALERT_LIGHTBOX_PARAGRAPH" />
            )}
          </CreateAlertLightboxParagraph>
        </div>

        {!didCreateAlert ? (
          <>
            <FiltersSummary {...summary} />

            {isLoggedOut && (
              <CreateAlertLightboxLOContent data-cy="search-alert-form-lo">
                <FormField
                  size="medium"
                  value={email}
                  onChange={(event) => onChangeEmail(event.target.value)}
                  label={intl.formatMessage({ id: 'JOBS_CREATE_SEARCH_ALERT_LIGHTBOX_EMAIL_INPUT_LABEL' })}
                  error={showEmailError}
                  helperText={showEmailError ? intl.formatMessage({ id: 'SE_AGENTS_INVALID_EMAIL_ERROR' }) : ''}
                />

                <FormattedMessage
                  id="JOBS_SEARCH_ALERTS_MODAL_LEGAL_TEXT"
                  values={{
                    a1: (chunks) => (
                      <Link to={routes.terms} target="_blank">
                        <strong>{chunks}</strong>
                      </Link>
                    ),
                    a2: (chunks) => (
                      <Link to={routes.privacy} target="_blank">
                        <strong>{chunks}</strong>
                      </Link>
                    ),
                  }}
                />
              </CreateAlertLightboxLOContent>
            )}
          </>
        ) : null}

        <CreateAlertLightboxButtonsWrapper>
          <Button
            variant="primary"
            size="medium"
            onClick={didCreateAlert ? onCancel('okay') : handleOnCreate}
            data-cy="job-posting-alert-lightbox-confirm-button"
          >
            <FormattedMessage
              id={
                didCreateAlert
                  ? 'JOBS_POSTING_CREATED_ALERT_LIGHTBOX_CONFIRM'
                  : 'JOBS_POSTING_CREATE_ALERT_LIGHTBOX_CONFIRM'
              }
            />
          </Button>
          {!didCreateAlert && (
            <TextButton size="medium" onClick={onCancel('cancel')}>
              <FormattedMessage id="JOBS_POSTING_CREATE_ALERT_LIGHTBOX_CANCEL" />
            </TextButton>
          )}
        </CreateAlertLightboxButtonsWrapper>
      </CreateAlertLightboxContent>
    </PopUpWindow>
  );
};
